<template>
  <div class="login-page">
    <!-- 背景装饰 -->
    <div class="bg-layer">
      <div class="bg-gradient"></div>
      <div class="bg-mesh"></div>
      <div v-if="!shouldReduceEffects" class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
      </div>
    </div>

    <!-- 主内容 -->
    <div class="login-wrapper">
      <!-- 左侧品牌区 -->
      <div class="brand-section">
        <div class="brand-content">
          <!-- Logo位置 -->
          <div class="brand-logo">
            <img src="/img/logo-white.png" alt="富福德正">
          </div>
          <p class="brand-tagline">智能债务化解管理平台</p>
          
          <div class="brand-features">
            <div class="feature-item">
              <div class="feature-icon">
                <el-icon><UserFilled /></el-icon>
              </div>
              <div class="feature-text">
                <h4>智能债务化解</h4>
                <p>专业团队量身定制还债方案，帮助债务人科学规划、逐步化解债务，走出困境重获财务自由</p>
              </div>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <el-icon><DataLine /></el-icon>
              </div>
              <div class="feature-text">
                <h4>亲友协同助力</h4>
                <p>邀请亲朋好友共同参与债务化解，汇聚爱心力量，让还债之路不再孤单，加速走向无债人生</p>
              </div>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <el-icon><Upload /></el-icon>
              </div>
              <div class="feature-text">
                <h4>消费积分变现</h4>
                <p>日常消费即可积累积分，积分可直接抵扣债务金额，让每一笔消费都为还债目标添砖加瓦</p>
              </div>
            </div>
          </div>
          
          <!-- 联系方式 - 放在brand-content内部，居中于消费积分变现 -->
          <div class="contact-section">
            <p>联系地址：海南省三亚市吉阳区吉阳大道212号三亚市互联网双创中心</p>
            <p>联系电话：17792260666</p>
          </div>
        </div>
      </div>

      <!-- 右侧登录区 -->
      <div class="login-section">
        <div class="login-card">
          <div class="login-header">
            <h2>欢迎回来</h2>
            <p>请登录您的管理员账号</p>
          </div>

          <el-form 
            :model="loginForm" 
            :rules="loginRules" 
            ref="loginFormRef"
            class="login-form"
            @submit.prevent="handleLogin"
          >
            <el-form-item prop="mobile">
              <div class="input-group">
                <label>手机号</label>
                <el-input 
                  v-model="loginForm.mobile" 
                  placeholder="请输入管理员手机号"
                  :prefix-icon="Phone"
                  size="large"
                />
              </div>
            </el-form-item>
            
            <el-form-item prop="password">
              <div class="input-group">
                <label>密码</label>
                <el-input 
                  v-model="loginForm.password" 
                  type="password"
                  placeholder="请输入密码"
                  :prefix-icon="Lock"
                  show-password
                  size="large"
                  @keyup.enter="handleLogin"
                />
              </div>
            </el-form-item>

            <el-form-item>
              <el-button 
                type="primary" 
                class="login-btn"
                :loading="loading"
                @click="handleLogin"
              >
                <span v-if="!loading">登 录</span>
                <span v-else>登录中...</span>
              </el-button>
            </el-form-item>
          </el-form>

          <div class="login-footer">
            <div class="footer-divider">
              <span>安全登录</span>
            </div>
            <p class="security-tips">
              <el-icon><Lock /></el-icon>
              您的信息将被安全加密传输
            </p>
          </div>
        </div>
        
        <!-- 版权信息 - 居中于登录卡片 -->
        <div class="copyright-section">
          <p class="footer-text"><a href="https://www.ffdzheng.com" target="_blank">© 2026 海南富福德正网络科技有限公司</a></p>
          <p class="footer-text">Powered by ToryLiu · V 0.2.0</p>
          <p class="footer-text agreement-links">
            <a href="javascript:;" @click="showAgreement('user')">用户协议</a>
            <span class="divider">|</span>
            <a href="javascript:;" @click="showAgreement('privacy')">隐私政策</a>
          </p>
        </div>
        
        <!-- 协议弹窗 -->
        <el-dialog 
          v-model="agreementDialog.visible" 
          :title="agreementDialog.title"
          width="600px"
          class="agreement-dialog"
        >
          <div class="agreement-content" v-html="agreementDialog.content"></div>
        </el-dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { Phone, Lock, UserFilled, DataLine, Upload } from '@element-plus/icons-vue'

const router = useRouter()
const store = useStore()
const loginFormRef = ref(null)
const loading = ref(false)
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
const shouldReduceEffects = computed(() => loading.value || prefersReducedMotion.matches)

// 协议弹窗
const agreementDialog = reactive({
  visible: false,
  title: '',
  content: ''
})

// 显示协议
const showAgreement = async (type) => {
  try {
    const res = await fetch(`/api/settings/agreement/${type}`)
    const data = await res.json()
    if (data.code === 200) {
      agreementDialog.title = data.data.type
      agreementDialog.content = data.data.content.replace(/\n/g, '<br>')
      agreementDialog.visible = true
    }
  } catch (e) {
    console.error('获取协议失败', e)
  }
}

const loginForm = reactive({
  mobile: '',
  password: ''
})

const loginRules = {
  mobile: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
  ]
}

const handleLogin = async () => {
  try {
    await loginFormRef.value.validate()
    loading.value = true
    
    const response = await store.dispatch('auth/login', {
      mobile: loginForm.mobile,
      password: loginForm.password
    })
    
    ElMessage.success(response.message || '登录成功！')
    router.push('/')
    
  } catch (error) {
    console.error('登录失败:', error)
    ElMessage.error(error.message || '登录失败，请检查手机号和密码')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const token = store.getters['auth/token']
  if (token) {
    router.push('/')
  }
})
</script>

<style scoped>
/* ========== 页面容器 ========== */
.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
}

/* ========== 背景层 ========== */
.bg-layer {
  position: fixed;
  inset: 0;
  z-index: 0;
}

.bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0c1222 0%, #1a365d 50%, #0f2744 100%);
}

.bg-mesh {
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.08) 0%, transparent 50%);
}

@media (prefers-reduced-motion: reduce) {
  .floating-shapes {
    display: none;
  }
}

.floating-shapes {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.shape-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #0ea5e9, #10b981);
  top: -100px;
  left: -100px;
  animation: float 25s ease-in-out infinite;
}

.shape-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #8b5cf6, #0ea5e9);
  bottom: -50px;
  right: 20%;
  animation: float 30s ease-in-out infinite reverse;
}

.shape-3 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #10b981, #0ea5e9);
  top: 40%;
  left: 30%;
  animation: float 20s ease-in-out infinite;
}

.shape-4 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #f59e0b, #10b981);
  top: 20%;
  right: 10%;
  animation: float 35s ease-in-out infinite reverse;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(20px, 30px) scale(1.05); }
  50% { transform: translate(-10px, 20px) scale(0.95); }
  75% { transform: translate(15px, -15px) scale(1.02); }
}

/* ========== 主布局 ========== */
.login-wrapper {
  display: flex;
  flex: 1;
  width: 100%;
  position: relative;
  z-index: 1;
  min-height: 100vh;
}

/* ========== 左侧品牌区 ========== */
.brand-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 5%;
  padding-right: 5%;
  position: relative;
}

.brand-section::before {
  content: '';
  position: absolute;
  right: 0;
  top: 10%;
  bottom: 10%;
  width: 1px;
  background: linear-gradient(
    180deg, 
    transparent 0%, 
    rgba(255,255,255,0.1) 20%,
    rgba(255,255,255,0.1) 80%,
    transparent 100%
  );
}

.brand-content {
  max-width: 420px;
  width: 100%;
  color: white;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
}

.brand-logo {
  margin-bottom: 16px;
}

.brand-logo img {
  height: 48px;
  width: auto;
  filter: brightness(1.1);
}

.brand-tagline {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 36px;
  letter-spacing: 1px;
}

.brand-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateX(6px);
}

.feature-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #10b981;
}

.feature-text h4 {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #f1f5f9;
}

.feature-text p {
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.6;
}

/* ========== 联系方式 - 在brand-content底部居中 ========== */
.contact-section {
  margin-top: auto;
  padding-top: 36px;
  text-align: center;
}

.contact-section p {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 6px 0;
  line-height: 1.6;
}

/* ========== 右侧登录区 ========== */
.login-section {
  width: 480px;
  min-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  padding-left: 60px;
  position: relative;
}

.login-card {
  width: 100%;
  max-width: 400px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
}

.login-header {
  text-align: center;
  margin-bottom: 36px;
}

.login-header h2 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
}

.login-header p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* ========== 表单样式 ========== */
.login-form :deep(.el-form-item) {
  margin-bottom: 24px;
}

.input-group {
  width: 100%;
}

.input-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.login-form :deep(.el-input__wrapper) {
  border-radius: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  box-shadow: none;
  transition: all 0.25s ease;
  padding: 4px 16px;
}

.login-form :deep(.el-input__wrapper:hover) {
  border-color: #0ea5e9;
  background: #ffffff;
}

.login-form :deep(.el-input__wrapper.is-focus) {
  border-color: #0ea5e9;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
}

.login-form :deep(.el-input__inner) {
  font-size: 15px;
  color: #1e293b;
}

.login-form :deep(.el-input__prefix-inner) {
  color: #94a3b8;
  font-size: 18px;
}

/* ========== 登录按钮 ========== */
.login-btn {
  width: 100%;
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.4);
  transition: all 0.3s ease;
  letter-spacing: 2px;
}

.login-btn:hover {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(14, 165, 233, 0.5);
}

.login-btn:active {
  transform: translateY(0);
}

/* ========== 底部区域 ========== */
.login-footer {
  margin-top: 28px;
}

.footer-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.footer-divider::before,
.footer-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
}

.footer-divider span {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}

.security-tips {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

.security-tips .el-icon {
  font-size: 14px;
}

/* ========== 版权信息 - 居中于登录卡片 ========== */
.copyright-section {
  margin-top: 40px;
  text-align: center;
  width: 100%;
  max-width: 400px;
}

/* ========== 底部文字样式 ========== */
.footer-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  margin: 0;
}

.footer-text a {
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-text a:hover {
  color: rgba(255, 255, 255, 0.8);
}

.agreement-links .divider {
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.3);
}

.agreement-dialog .agreement-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 20px;
  line-height: 1.8;
  white-space: pre-wrap;
  font-size: 14px;
  color: #333;
}

/* ========== 响应式设计 ========== */
@media (max-width: 1024px) {
  .brand-section {
    display: none;
  }
  
  .login-section {
    width: 100%;
    min-width: auto;
    padding: 24px;
    min-height: 100vh;
    padding-bottom: 100px;
  }
  
  .login-card {
    max-width: 100%;
  }
  
  .copyright-section {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .login-card {
    padding: 28px 24px;
  }
  
  .login-header h2 {
    font-size: 24px;
  }
}
</style>
